import { useMemo } from 'react';

import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';
import { PRODUCT_FORM_VALUES, type ProductForm } from '@components/Product/types';
import {
  createResourceRouterSearch,
  createResourceSearchParser,
  type ResourceListSearchParams,
} from '@hooks/resourceListPage';

// The sortable columns' ids, sent as /api/bundles' sort_by. Price before GST
// sorts on base_price, the column its price_excluding_gst is read from.
type BundleSortColumn = 'name' | 'format' | 'base_price';

const BUNDLE_SORT_COLUMNS: ReadonlyArray<BundleSortColumn> = ['name', 'format', 'base_price'];

// The shared list params plus the format filter, which the shared factory
// has no slot for.
export interface BundlesSearchParams extends ResourceListSearchParams<BundleSortColumn> {
  formats: ProductForm[];
}

// Name A to Z matches the backend's default order for /api/bundles.
export const BUNDLES_SEARCH_DEFAULTS: BundlesSearchParams = {
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: 'name',
  direction: 'asc',
  formats: [],
};

const parseListSearch = createResourceSearchParser<BundleSortColumn>({
  defaults: BUNDLES_SEARCH_DEFAULTS,
  validSorts: BUNDLE_SORT_COLUMNS,
  requireSortForDirection: true,
});

const VALID_FORMATS: ReadonlySet<string> = new Set(PRODUCT_FORM_VALUES);

// Keeps only known formats, so a stale or hand-edited URL can't send the API
// a value no bundle can have.
function toFormats(raw: unknown): ProductForm[] {
  const values = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [];
  return values.filter((v): v is ProductForm => typeof v === 'string' && VALID_FORMATS.has(v));
}

export function parseBundlesSearch(raw: Record<string, unknown>): BundlesSearchParams {
  return { ...parseListSearch(raw), formats: toFormats(raw.formats) };
}

const useListRouterSearch = createResourceRouterSearch<BundleSortColumn>('/bundles');

export function useBundlesRouterSearch() {
  const router = useListRouterSearch();
  return useMemo(
    () => ({
      ...router,
      // The route's validateSearch is parseBundlesSearch, so formats is
      // always present.
      search: router.search as BundlesSearchParams,
      setFormats: (formats: ProductForm[]) =>
        router.navigate({ formats, page: 1 } as Partial<BundlesSearchParams>),
    }),
    [router],
  );
}
